import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { hasPermission } from '../utils/permissions';
import { daysUntil, LICENSE_EXPIRY_NOTICE_DAYS } from '../utils/license';

/**
 * Bar below the header for an expiring or expired licence.
 * Expired: everyone sees it, because saving is blocked for everyone (read-only).
 * Expiring within the notice period: only users who can open the licence page.
 */
export default function LicenseNotice() {
  const { t, i18n } = useTranslation(['common']);
  const userProfile = useStore((s) => s.userProfile);

  if (!userProfile) {
    return null;
  }

  const validUntil = userProfile.licenseValidUntil;
  const formatDate = (iso: string) => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Intl.DateTimeFormat(i18n.language === 'de' ? 'de-DE' : 'en-US', { dateStyle: 'medium' })
      .format(new Date(year, month - 1, day));
  };

  if (userProfile.licenseReadOnly) {
    return (
      <div className="w-full px-5 py-2 bg-red-100 text-red-800 text-sm border-b" role="alert" data-testid="license-notice">
        {t('license.notice.expired', { date: validUntil ? formatDate(validUntil) : '' })}
      </div>
    );
  }

  if (!validUntil || !hasPermission('system:settings:view', userProfile)) {
    return null;
  }
  const days = daysUntil(validUntil);
  if (days > LICENSE_EXPIRY_NOTICE_DAYS) {
    return null;
  }

  return (
    <div className="w-full px-5 py-2 bg-yellow-100 text-yellow-900 text-sm border-b" role="status" data-testid="license-notice">
      {t('license.notice.expiring', { count: days, date: formatDate(validUntil) })}
    </div>
  );
}
